import React from 'react';
import {
    ChoiceGroup,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    getTheme,
    Icon,
    IChoiceGroupOption,
    Link as FluentLink,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    Toggle
} from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import {
    useGetDistributorCandidatesQuery,
    useSetupDistributorFolioMutation
} from '../../store/endpoints/FolioAdminEndpoints';
import {
    useGetExtensionFolioQuery,
    usePatchExtensionFolioCommonFieldsMutation,
    usePutExtensionFolioMutation,
    useValidateExtensionFolioMutation
} from '../../store/Api';
import { useSetGettingStartedStepStatusMutation } from '../../store/endpoints/GettingStartedEndpoints';
import { ExtensionStaticDataForm } from '../ExtensionInstallation/ExtensionDataForm';
import { IAdditionalData, IDistributorCandidate, IHelpLink } from '../../models/Extension';
import { DISTRIBUTOR_STRINGS as S } from './wizardStrings';

// Connecting a distributor, as a guided flow rather than a form.
//
// The shape follows the questions an operator actually has, in the order they have them: which
// distributor, what should it do, do I even have the credentials yet, and then -- only then -- the
// fields. The step that earns its place is the third one: a distributor issues different credentials per
// capability from different places in their portal, so "not yet" is the common answer on day one, and the
// old single screen answered it by showing a form the person could not fill in. Here it is a real branch
// that hands them the right links and lets them leave without losing the choices they made.
//
// Credentials are entered here but not handled here: the fields, their masking, and the unchanged-secret
// round-trip all come from ExtensionStaticDataForm, and saving goes through the same folio mutations the
// CRM flow uses. This component picks steps and nothing else.

const theme = getTheme();

type Step = 'pick' | 'capabilities' | 'readiness' | 'credentials' | 'done';

/**
 * One credential screen. Discriminated on `kind` so the capability variant carries its identifier as a
 * plain string — the shared screen has none, and a single optional field would mean asserting it away at
 * every use.
 */
type CredentialPage =
    | { kind: 'shared', title: string, intro?: string, fields: IAdditionalData[], helpLinks?: IHelpLink[] }
    | {
        kind: 'capability', identifier: string, title: string, intro?: string,
        fields: IAdditionalData[], helpLinks?: IHelpLink[],
        /** Whether THIS capability can be checked — they do not all share one validator. */
        canVerify: boolean
    };

const CAPABILITY_ICONS: Record<string, string> = {
    import: 'CloudDownload',
    realtime: 'Sync',
    procurement: 'ShoppingCart',
};

const KIND_LABELS: Record<string, string> = {
    import: S.kindImport,
    realtime: S.kindRealtime,
    procurement: S.kindProcurement,
};

const STATUS_COLORS: Record<string, string> = {
    complete: theme.palette.green,
    needsReview: theme.palette.redDark,
    inProgress: theme.palette.neutralTertiary,
};

const iconFor = (category?: string) => CAPABILITY_ICONS[category ?? ''] ?? 'PlugConnected';

/** "How to get these credentials" pointers. */
const HelpLinks: React.FC<{ links?: IHelpLink[], heading?: string }> = ({ links, heading }) => {
    const usable = (links ?? []).filter(l => !!l.url);
    if (usable.length === 0) return null;
    return (
        <Stack tokens={{ childrenGap: '0.35em' }}>
            {heading && (
                <Text variant='small' styles={{ root: { fontWeight: 600 } }}>{heading}</Text>
            )}
            {usable.map((link, index) => (
                <FluentLink key={`${link.url}-${index}`} href={link.url} target='_blank' rel='noopener noreferrer'>
                    {link.text || link.url}
                </FluentLink>
            ))}
        </Stack>
    );
};

/**
 * A distributor as one choice. Deliberately sparse: logo, name, what it is, and a one-line summary of the
 * kinds of thing it can do. The detail that used to live here -- every capability with its own
 * documentation link -- belongs to the steps that follow, where it is about a choice the person has
 * already made.
 */
const DistributorCard: React.FC<{
    candidate: IDistributorCandidate,
    onSelect: () => void
}> = ({ candidate, onSelect }) => {
    const kinds = Array.from(new Set((candidate.capabilities ?? [])
        .map(c => KIND_LABELS[c.category ?? ''])
        .filter(Boolean)));
    const status = candidate.status;

    return (
        <div
            role='button'
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => { if (e.key === 'Enter') onSelect(); }}
            style={{
                border: `1px solid ${theme.palette.neutralLight}`,
                borderRadius: 4,
                padding: '1em',
                width: 250,
                cursor: 'pointer',
                background: theme.palette.white,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 176,
            }}
        >
            {/* Grows so the action below it sits on a common baseline across cards. Note the action is a
                SIBLING of this stack: Fluent writes childrenGap as a margin-top on every non-first
                child, which would overwrite a marginTop:auto used inside it. */}
            <Stack tokens={{ childrenGap: '0.5em' }} styles={{ root: { flexGrow: 1, minHeight: 0 } }}>
                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.75em' }}>
                    {candidate.image
                        ? <img src={candidate.image} alt='' style={{ height: 30, maxWidth: 96, objectFit: 'contain' }} />
                        : <Icon iconName='Trackers' style={{ fontSize: 24 }} />}
                    <Text style={{ fontWeight: 600 }}>{candidate.name}</Text>
                </Stack>

                {candidate.description && (
                    <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                        {candidate.description}
                    </Text>
                )}

                {kinds.length > 0 && (
                    <Text variant='small' style={{ color: theme.palette.neutralTertiary }}>
                        {S.offers(kinds)}
                    </Text>
                )}

                {status && (
                    <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.4em' }}>
                        <span
                            aria-hidden
                            style={{
                                width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                                backgroundColor: STATUS_COLORS[status] ?? theme.palette.neutralTertiary,
                            }}
                        />
                        <Text variant='small' style={{
                            color: status === 'needsReview' ? theme.palette.redDark : theme.palette.neutralSecondary
                        }}>
                            {S.status[status]}
                        </Text>
                    </Stack>
                )}
            </Stack>

            <div style={{ paddingTop: '0.75em' }}>
                <DefaultButton
                    iconProps={{ iconName: 'Link' }}
                    text={candidate.installed ? S.openConnection : S.connect}
                    onClick={(e) => { e.stopPropagation(); onSelect(); }}
                />
            </div>
        </div>
    );
};

export interface IDistributorSetupDialogProps {
    isOpen: boolean;
    /**
     * Open straight onto this distributor instead of the picker, resuming from whatever is stored.
     * The checklist passes it so clicking an unfinished connection carries on with that connection.
     */
    initialCandidateId?: string;
    onDismiss: () => void;
}

const DistributorSetupDialog: React.FC<IDistributorSetupDialogProps> = ({ isOpen, initialCandidateId, onDismiss }) => {
    const history = useHistory();
    const candidates = useGetDistributorCandidatesQuery(undefined, { skip: !isOpen });
    const items = candidates.data?.items ?? [];

    const [setupDistributor] = useSetupDistributorFolioMutation();
    const [saveCommonFields] = usePatchExtensionFolioCommonFieldsMutation();
    const [putFolio] = usePutExtensionFolioMutation();
    const [validateFolio] = useValidateExtensionFolioMutation();
    const [setStepStatus] = useSetGettingStartedStepStatusMutation();

    const [step, setStep] = React.useState<Step>('pick');
    const [selected, setSelected] = React.useState<IDistributorCandidate | undefined>();
    const [chosen, setChosen] = React.useState<string[]>([]);
    const [hasCredentials, setHasCredentials] = React.useState<string | undefined>();
    const [shared, setShared] = React.useState<{ [key: string]: string }>({});
    const [perCapability, setPerCapability] = React.useState<{ [id: string]: { [key: string]: string } }>({});
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>();
    const [verified, setVerified] = React.useState<boolean | undefined>();
    // Keyed by capability identifier, so a failure is shown next to the fields that caused it.
    // Which credential screen we are on. The credentials are walked one set at a time: a distributor
    // asks for three unrelated accounts, and showing them together is the wall of fields this flow exists
    // to break up.
    const [page, setPage] = React.useState(0);
    // Which capabilities came back verified, for the summary at the end.
    const [verifiedCapabilities, setVerifiedCapabilities] = React.useState<string[]>([]);
    // The capability that just passed, shown at the top of the screen that follows. Advancing in silence
    // gave no sign the check had happened at all, let alone that it succeeded.
    const [justVerified, setJustVerified] = React.useState<string | undefined>();

    // The installed folio's detail view: the credential fields to render come from here, not from the
    // candidate read model, because only this carries what is already stored.
    const folio = useGetExtensionFolioQuery(selected?.id ?? '', {
        skip: !selected?.id || step === 'pick' || step === 'capabilities',
    });
    const folioData = folio.data;
    const source = folioData?.sourceExtensionFolio;

    /** The capabilities the operator turned on, in the folio's own order. */
    const chosenCapabilities = (selected?.capabilities ?? []).filter(c => chosen.includes(c.identifier));

    /**
     * Where an unfinished setup picks up.
     *
     * Read off what is stored rather than off a step somebody was on: the wizard is reachable from the
     * checklist in a later session, after a reload, or on another machine, and none of those carry a
     * remembered position. Enabled capabilities are the evidence that the "what should it do" question
     * was already answered, which is the only thing that decides whether it needs asking again.
     */
    const resumedFor = React.useRef<string | undefined>(undefined);
    React.useEffect(() => {
        if (!isOpen || !initialCandidateId) return;
        if (resumedFor.current === initialCandidateId) return;
        const candidate = items.find(c => c.id === initialCandidateId);
        if (!candidate) return;

        resumedFor.current = initialCandidateId;
        setSelected(candidate);
        const enabled = (candidate.capabilities ?? []).filter(c => c.enabled).map(c => c.identifier);
        setChosen(enabled.length > 0 ? enabled : (candidate.defaultSubExtensions ?? []));
        if (enabled.length > 0) {
            // They got as far as recording what this connection should do, so the credentials are what
            // is left. Skipping the readiness question is deliberate: they have already answered it.
            setHasCredentials('yes');
            setStep('credentials');
        } else {
            setStep('capabilities');
        }
        // eslint-disable-next-line
    }, [isOpen, initialCandidateId, items]);

    // A fresh open with no candidate must not inherit the last resume.
    React.useEffect(() => {
        if (!isOpen) resumedFor.current = undefined;
    }, [isOpen]);

    /**
     * Moving between screens clears the error: it described the screen being left, and leaving it up makes
     * the next screen look broken before it has been touched.
     */
    const goToStep = (next: Step) => {
        setError(undefined);
        setJustVerified(undefined);
        setStep(next);
    };

    const goToPage = (next: number) => {
        setError(undefined);
        setJustVerified(undefined);
        setPage(next);
    };

    const reset = () => {
        setStep('pick');
        setSelected(undefined);
        setChosen([]);
        setHasCredentials(undefined);
        setShared({});
        setPerCapability({});
        setError(undefined);
        setVerified(undefined);
        setPage(0);
        setVerifiedCapabilities([]);
        setJustVerified(undefined);
    };

    const close = () => { reset(); onDismiss(); };

    const pick = (candidate: IDistributorCandidate) => {
        setSelected(candidate);
        // Pre-tick what the folio itself says a quick setup would turn on, so the common case is one
        // click rather than a checklist to work through.
        const defaults = candidate.defaultSubExtensions ?? [];
        const enabled = (candidate.capabilities ?? []).filter(c => c.enabled).map(c => c.identifier);
        setChosen(enabled.length > 0 ? enabled : defaults);
        goToStep('capabilities');
    };

    const toggle = (identifier: string, on: boolean) =>
        setChosen(current => on ? [...current, identifier] : current.filter(c => c !== identifier));

    /**
     * Creates the connection and records exactly what it should do, in one call.
     *
     * Deliberately not the CRM quick setup: that route rejects any folio that is not a configured CRM,
     * and the rejection used to surface here as a credential error on a screen that had not yet asked
     * for credentials. One call also means the connection cannot end up installed with the wrong
     * capabilities because a second call failed.
     */
    const prepare = async (): Promise<boolean> => {
        if (!selected?.id) return false;
        setBusy(true);
        setError(undefined);
        try {
            await setupDistributor({ id: selected.id, capabilities: chosen }).unwrap();
            // The credential form reads the installed detail view, which only exists now.
            await folio.refetch().unwrap().catch(() => undefined);
            return true;
        } catch (e) {
            // The server's own message names what actually went wrong. Anything else here is a failure
            // to PREPARE the connection, which is a different thing from credentials being rejected —
            // saying so was the bug.
            const data = (e as { data?: { message?: string } })?.data;
            setError(data?.message ?? S.setupFailed);
            return false;
        } finally {
            setBusy(false);
        }
    };

    const goToCredentials = async () => {
        if (await prepare()) setStep('credentials');
    };

    /** Leaving on the "not yet" path still records that the work was started. */
    const comeBackLater = () => {
        void setStepStatus({ id: 'connect-distributors', status: 'inProgress' });
        close();
    };

    /**
     * The credential screens, in the order they are asked for.
     *
     * A shared set comes first where the folio has one (a CRM, or the API pair a distributor uses for both
     * real-time pricing and ordering), then one screen per chosen capability that asks for anything of its
     * own. A capability with no fields of its own never gets a screen -- Griffin IT asks for nothing, so it
     * would be an empty page with a Continue button on it.
     */
    const credentialPages = React.useMemo(() => {
        const sharedFields = source?.sharedFields ?? [];
        const pages: CredentialPage[] = [];

        if (sharedFields.length > 0) {
            pages.push({
                kind: 'shared',
                title: S.credentialsShared,
                intro: S.credentialsSharedIntro,
                fields: sharedFields,
                helpLinks: source?.helpLinks,
            });
        }

        for (const capability of chosenCapabilities) {
            const fields = source?.subExtensions?.[capability.identifier]?.additionalData ?? [];
            if (fields.length === 0) continue;
            pages.push({
                kind: 'capability',
                identifier: capability.identifier,
                title: capability.name ?? capability.identifier,
                intro: capability.description,
                fields,
                helpLinks: capability.helpLinks,
                canVerify: capability.hasValidation,
            });
        }

        return pages;
        // eslint-disable-next-line
    }, [source, chosen]);

    const currentPage = credentialPages[page];
    const isLastPage = page >= credentialPages.length - 1;

    /**
     * Saves the screen in front of the operator and, for a capability, checks it there and then.
     *
     * Checking per screen rather than all at the end is the point of splitting them up: a rejected FTP
     * password is reported while its two fields are still on screen, instead of three screens later next
     * to credentials that were fine.
     */
    const submitPage = async () => {
        if (!selected?.id || !folioData?.id || !currentPage) return;
        setBusy(true);
        setError(undefined);
        setJustVerified(undefined);
        let passed: string | undefined;
        try {
            if (currentPage.kind === 'shared') {
                await saveCommonFields({
                    extensionId: selected.id,
                    fields: { ...folioData.commonInstalledFields, ...shared },
                }).unwrap();
            } else {
                const identifier = currentPage.identifier;
                await putFolio({
                    extensionId: selected.id,
                    request: {
                        ...folioData,
                        enabledSubExtensions: chosen,
                        subExtensionInstalledFields: {
                            ...folioData.subExtensionInstalledFields,
                            // Stored values first, then whatever was typed on this screen. Sending only
                            // what the form emitted meant a screen nobody edited posted an empty set,
                            // which read as "clear these" rather than "leave them alone".
                            [identifier]: {
                                ...(folioData.subExtensionInstalledFields?.[identifier] ?? {}),
                                ...(perCapability[identifier] ?? {}),
                            },
                        },
                    },
                }).unwrap();

                if (currentPage.canVerify) {
                    const result = await validateFolio({
                        id: selected.id,
                        // Only this capability. A call naming several would return one verdict covering
                        // credentials it never used, and the server stamps whatever the call says it covered.
                        request: {
                            additionalData: shared,
                            folioId: folioData.id,
                            validationActions: [identifier],
                        },
                    }).unwrap();

                    if (!result?.success) {
                        // Stay here: the fields that need fixing are the ones on screen.
                        setError(result?.message || S.validateFailed);
                        return;
                    }
                    setVerifiedCapabilities(current =>
                        current.includes(identifier) ? current : [...current, identifier]);
                    passed = currentPage.title;
                }
            }

            if (isLastPage) {
                // "Verified" means every capability that COULD be checked was, and passed.
                const checkable = credentialPages.filter(p => p.kind === 'capability' && p.canVerify);
                setVerified(checkable.length > 0);
                setStep('done');
                return;
            }
            goToPage(page + 1);
            // After goToPage, which clears it: this is the acknowledgement for the screen just finished,
            // carried onto the one now in front of them.
            setJustVerified(passed);
        } catch (e) {
            const data = (e as { data?: { message?: string } })?.data;
            setError(data?.message ?? S.validateFailed);
        } finally {
            setBusy(false);
        }
    };



    const renderPick = () => {
        if (candidates.isLoading || candidates.isFetching) return <Spinner label={S.loading} />;
        if (candidates.isError) {
            return <MessageBar messageBarType={MessageBarType.error}>{S.loadFailed}</MessageBar>;
        }
        if (items.length === 0) return <Text>{S.empty}</Text>;
        return (
            <Stack horizontal wrap tokens={{ childrenGap: '1em' }}>
                {items.map(candidate => (
                    <DistributorCard key={candidate.id} candidate={candidate} onSelect={() => pick(candidate)} />
                ))}
            </Stack>
        );
    };

    const renderCapabilities = () => (
        <Stack tokens={{ childrenGap: '0.75em' }}>
            <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{S.capabilitiesIntro}</Text>
            {(selected?.capabilities ?? []).map(capability => (
                <div
                    key={capability.identifier}
                    style={{
                        border: `1px solid ${theme.palette.neutralLight}`,
                        borderRadius: 4,
                        padding: '0.75em',
                    }}
                >
                    <Stack horizontal horizontalAlign='space-between' verticalAlign='center' tokens={{ childrenGap: '1em' }}>
                        <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.75em' }}>
                            <Icon iconName={iconFor(capability.category)} style={{ fontSize: 18, color: theme.palette.neutralSecondary }} />
                            <Stack style={{ minWidth: 0 }}>
                                <Text style={{ fontWeight: 600 }}>{capability.name}</Text>
                                {capability.description && (
                                    <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                        {capability.description}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                        <Toggle
                            ariaLabel={capability.name}
                            checked={chosen.includes(capability.identifier)}
                            onChange={(_e, checked) => toggle(capability.identifier, !!checked)}
                            styles={{ root: { marginBottom: 0 } }}
                        />
                    </Stack>
                </div>
            ))}
            {chosen.length === 0 && (
                <Text variant='small' style={{ color: theme.palette.redDark }}>{S.capabilitiesNone}</Text>
            )}
        </Stack>
    );

    const readinessOptions: IChoiceGroupOption[] = [
        { key: 'yes', text: S.readinessYes },
        { key: 'no', text: S.readinessNo },
    ];

    const renderReadiness = () => (
        <Stack tokens={{ childrenGap: '1em' }}>
            <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{S.readinessIntro}</Text>
            <ChoiceGroup
                options={readinessOptions}
                selectedKey={hasCredentials}
                onChange={(_e, option) => setHasCredentials(option?.key)}
            />
            {hasCredentials === 'no' && (
                <Stack tokens={{ childrenGap: '0.75em' }}>
                    <Separator alignContent='start'>
                        <span style={{ fontWeight: 600 }}>{S.readinessHelpTitle}</span>
                    </Separator>
                    {/* One block per chosen capability, because that is the level at which the
                        credentials differ -- an FTP account and an API key are requested in different
                        places, and a single folio-level link cannot answer for both. */}
                    {chosenCapabilities.map(capability => (
                        <Stack key={capability.identifier} tokens={{ childrenGap: '0.25em' }}>
                            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.5em' }}>
                                <Icon iconName={iconFor(capability.category)} style={{ fontSize: 14, color: theme.palette.neutralSecondary }} />
                                <Text variant='small' style={{ fontWeight: 600 }}>
                                    {S.readinessHelpFor(capability.name ?? capability.identifier)}
                                </Text>
                            </Stack>
                            {(capability.helpLinks ?? []).some(l => l.url)
                                ? <HelpLinks links={capability.helpLinks} />
                                : <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                    {S.readinessNoHelp}
                                </Text>}
                        </Stack>
                    ))}
                    <MessageBar messageBarType={MessageBarType.info}>{S.readinessSaved}</MessageBar>
                </Stack>
            )}
        </Stack>
    );

    /** One credential set at a time, with its own documentation beside it. */
    const renderCredentials = () => {
        if (folio.isLoading || folio.isFetching || !folioData) {
            return <Spinner size={SpinnerSize.large} label={S.loadingFields} />;
        }
        if (folio.isError || !currentPage) {
            // Without this the screen sat on a spinner forever when the folio read failed.
            return <MessageBar messageBarType={MessageBarType.error}>{S.loadFieldsFailed}</MessageBar>;
        }

        const stored = currentPage.kind === 'shared'
            ? folioData.commonInstalledFields
            : folioData.subExtensionInstalledFields?.[currentPage.identifier];
        const entered = currentPage.kind === 'shared'
            ? shared
            : perCapability[currentPage.identifier];

        return (
            <Stack tokens={{ childrenGap: '0.75em' }}>
                {justVerified && (
                    <MessageBar messageBarType={MessageBarType.success}>
                        {S.capabilityVerified(justVerified)}
                    </MessageBar>
                )}

                {credentialPages.length > 1 && (
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                            {S.credentialProgress(page + 1, credentialPages.length)}
                        </Text>
                        {/* A ticked list rather than a bare counter: it shows what has been done, not just
                            how far along, so finishing one screen visibly moves something. */}
                        <Stack horizontal wrap tokens={{ childrenGap: '0.75em' }}>
                            {credentialPages.map((p, index) => {
                                const done = index < page;
                                const current = index === page;
                                return (
                                    <Stack
                                        horizontal
                                        key={p.kind === 'capability' ? p.identifier : 'shared'}
                                        verticalAlign='center'
                                        tokens={{ childrenGap: '0.3em' }}
                                    >
                                        <Icon
                                            iconName={done ? 'CompletedSolid' : current ? 'CircleFill' : 'CircleRing'}
                                            style={{
                                                fontSize: done ? 12 : 8,
                                                color: done
                                                    ? theme.palette.green
                                                    : current
                                                        ? theme.palette.themePrimary
                                                        : theme.palette.neutralTertiary,
                                            }}
                                        />
                                        <Text
                                            variant='small'
                                            style={{
                                                color: current ? theme.palette.neutralPrimary : theme.palette.neutralSecondary,
                                                fontWeight: current ? 600 : 400,
                                            }}
                                        >
                                            {p.title}
                                        </Text>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Stack>
                )}

                {currentPage.kind === 'capability' && !currentPage.canVerify && (
                    <MessageBar messageBarType={MessageBarType.info}>{S.cannotValidate}</MessageBar>
                )}

                <Separator alignContent='start'>
                    <span style={{ fontWeight: 600 }}>{currentPage.title}</span>
                </Separator>
                {currentPage.intro && (
                    <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                        {currentPage.intro}
                    </Text>
                )}

                <ExtensionStaticDataForm
                    // Remounted per screen: the form seeds itself from staticData, so one instance reused
                    // across screens would carry the previous capability's values into the next.
                    key={currentPage.kind === 'capability' ? currentPage.identifier : 'shared'}
                    staticData={{ ...stored, ...entered }}
                    additionalFields={currentPage.fields}
                    extensionId={`folio_${source?.id ?? ''}_${currentPage.kind === 'capability' ? currentPage.identifier : 'shared'}`}
                    updateOnDisengage
                    onChange={(data) => {
                        if (currentPage.kind === 'shared') setShared(data);
                        else setPerCapability(current => ({ ...current, [currentPage.identifier]: data }));
                    }}
                />

                <HelpLinks links={currentPage.helpLinks} heading={S.helpHeading} />
            </Stack>
        );
    };

    const renderDone = () => (
        <Stack tokens={{ childrenGap: '1em' }}>
            <MessageBar messageBarType={verified ? MessageBarType.success : MessageBarType.info}>
                {verified ? S.doneVerified : S.doneNotVerified}
            </MessageBar>
            <Stack tokens={{ childrenGap: '0.35em' }}>
                {chosenCapabilities.map(capability => {
                    // A tick means this capability was actually checked and passed. A capability with no
                    // credentials of its own, or a connector that cannot be verified, is on but unproven —
                    // and the summary should not imply otherwise.
                    const proven = verifiedCapabilities.includes(capability.identifier);
                    return (
                        <Stack horizontal key={capability.identifier} verticalAlign='center' tokens={{ childrenGap: '0.5em' }}>
                            <Icon
                                iconName={proven ? 'CheckMark' : 'CircleRing'}
                                style={{ fontSize: 12, color: proven ? theme.palette.green : theme.palette.neutralTertiary }}
                            />
                            <Text variant='small'>{capability.name}</Text>
                            {!proven && (
                                <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                    {S.doneUnverifiedCapability}
                                </Text>
                            )}
                        </Stack>
                    );
                })}
            </Stack>
            <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{S.doneWhatNext}</Text>
        </Stack>
    );

    const title = () => {
        if (step === 'pick') return S.title;
        const name = selected?.name ?? '';
        if (step === 'capabilities') return S.capabilitiesTitle(name);
        if (step === 'readiness') return S.readinessTitle(name);
        if (step === 'credentials') return S.credentialsTitle(name);
        return S.doneTitle(name);
    };

    const footer = () => {
        switch (step) {
            case 'pick':
                return <DefaultButton onClick={close} text={S.close} />;
            case 'capabilities':
                return (<>
                    <PrimaryButton
                        text={S.capabilitiesNext}
                        disabled={chosen.length === 0}
                        onClick={() => goToStep('readiness')}
                    />
                    {/* A resumed session has no picker behind it to go back to. */}
                    {!initialCandidateId && (
                        <DefaultButton text={S.back} onClick={() => { setSelected(undefined); goToStep('pick'); }} />
                    )}
                </>);
            case 'readiness':
                return (<>
                    {hasCredentials === 'no'
                        ? <>
                            <PrimaryButton text={S.readinessHaveThemNow} disabled={busy} onClick={goToCredentials} />
                            <DefaultButton text={S.readinessComeBack} onClick={comeBackLater} />
                        </>
                        : <PrimaryButton
                            text={S.capabilitiesNext}
                            disabled={hasCredentials !== 'yes' || busy}
                            onClick={goToCredentials}
                        />}
                    <DefaultButton text={S.back} onClick={() => goToStep('capabilities')} />
                </>);
            case 'credentials':
                return (<>
                    <PrimaryButton
                        text={(() => {
                            const verifies = currentPage?.kind === 'capability' && currentPage.canVerify;
                            if (isLastPage) return verifies ? S.validateLabel : S.saveLabel;
                            return verifies ? S.verifyAndContinue : S.capabilitiesNext;
                        })()}
                        disabled={busy}
                        onClick={submitPage}
                    />
                    <DefaultButton
                        text={S.back}
                        // Back walks the credential screens before leaving the step, so the way out is
                        // the way in.
                        onClick={() => (page > 0 ? goToPage(page - 1) : goToStep('readiness'))}
                    />
                </>);
            default:
                return (<>
                    <PrimaryButton text={S.done} onClick={close} />
                    <DefaultButton
                        text={S.doneAddAnother}
                        onClick={() => { reset(); }}
                    />
                    <DefaultButton
                        text={S.openConnection}
                        onClick={() => {
                            const installedId = folioData?.id;
                            close();
                            if (installedId) {
                                history.push(`/settings/extensions?panel=extensionFolio&instance=${encodeURIComponent(installedId)}&panelTab=edit`);
                            }
                        }}
                    />
                </>);
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={close}
            minWidth={step === 'pick' ? 620 : 560}
            maxWidth={step === 'pick' ? 900 : 720}
            dialogContentProps={{
                type: DialogType.normal,
                title: title(),
                subText: step === 'pick' ? S.intro : undefined,
            }}
        >
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {busy && <Spinner size={SpinnerSize.small} label={S.validating} labelPosition='right' />}

            {step === 'pick' && renderPick()}
            {step === 'capabilities' && renderCapabilities()}
            {step === 'readiness' && renderReadiness()}
            {step === 'credentials' && renderCredentials()}
            {step === 'done' && renderDone()}

            <DialogFooter>{footer()}</DialogFooter>
        </Dialog>
    );
};

export default DistributorSetupDialog;
